import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Search, 
  Download, 
  Trash2, 
  FileText,
  Calendar,
  Filter,
  X,
  Pencil,
  Share2,
  User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { getAllDDR, deleteDDR } from "@/services/localDatabase";
import { shareDDRPdf } from "@/services/pdfGenerator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Truck } from "lucide-react";

const History = () => {
  const navigate = useNavigate();
  const [ddrs, setDdrs] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const [tipoFilter, setTipoFilter] = useState("tutti");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchDDRs();
  }, []);

  useEffect(() => {
    filterDDRs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dateFilter, tipoFilter, ddrs]);

  const fetchDDRs = async () => {
    try {
      const data = await getAllDDR();
      setDdrs(data);
      setFiltered(data);
    } catch (error) {
      console.error("Error fetching DDRs:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const filterDDRs = () => {
    let result = [...ddrs];
    
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(ddr => 
        ddr.specie?.toLowerCase().includes(s) ||
        ddr.destinazione?.nome?.toLowerCase().includes(s) ||
        String(ddr.numero_ddr).includes(s) ||
        ddr.lotto?.toLowerCase().includes(s) ||
        ddr.serie?.toLowerCase().includes(s)
      );
    }
    
    if (dateFilter) {
      const filterDate = format(dateFilter, "dd/MM/yyyy");
      result = result.filter(ddr => ddr.data_documento === filterDate);
    }
    
    // Filter by tipo produttore
    if (tipoFilter && tipoFilter !== "tutti") {
      result = result.filter(ddr => ddr.produttore?.tipo === tipoFilter);
    }
    
    setFiltered(result);
  };

  const handleDownload = async (ddr) => {
    try {
      const result = await downloadDDRPdf(ddr);
      if (result.path) {
        toast.success(`PDF salvato in Documenti/DDR/${result.filename}`);
      } else {
        toast.success("PDF scaricato");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Errore nel download");
    }
  };

  const handleShare = async (ddr) => {
    try {
      const result = await shareDDRPdf(ddr);
      if (result.success) {
        toast.success("PDF pronto per la condivisione");
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error sharing PDF:", error);
        toast.error("Errore nella condivisione");
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteDDR(deleteId);
      toast.success("DDR eliminato");
      fetchDDRs();
    } catch (error) {
      console.error("Error deleting DDR:", error);
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    if (dateString.includes('/')) return dateString;
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT');
  };

  const clearFilters = () => {
    setSearch("");
    setDateFilter(null);
    setTipoFilter("tutti");
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="history-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Storico DDR
          </h1>
          <p className="text-slate-500 mt-1">Archivio di tutti i documenti</p>
        </div>
        <div className="flex items-center gap-2 mt-4 md:mt-0">
          <Badge variant="outline" className="text-sm">
            {filtered.length} documenti
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6 border-slate-100">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  data-testid="search-input"
                  placeholder="Cerca per specie, destinatario, numero, serie..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Tipo Filter */}
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger className="w-[180px]" data-testid="tipo-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">
                  <span className="flex items-center gap-2">Tutti</span>
                </SelectItem>
                <SelectItem value="produttore">
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4 text-orange-500" /> Produttore
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[160px]" data-testid="date-filter-btn">
                  <Calendar className="w-4 h-4 mr-2" />
                  {dateFilter ? format(dateFilter, "dd/MM/yyyy", { locale: it }) : "Filtra data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarComponent
                  mode="single"
                  selected={dateFilter}
                  onSelect={setDateFilter}
                  locale={it}
                />
              </PopoverContent>
            </Popover>

            {(search || dateFilter || tipoFilter !== "tutti") && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-1" />
                Pulisci
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* DDR List */}
      <Card className="border-slate-100">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-barlow text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-500" />
            Elenco Documenti
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {ddrs.length === 0 ? "Nessun DDR presente" : "Nessun risultato trovato"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>N° DDR</th>
                    <th>Tipo</th>
                    <th>Data</th>
                    <th>Specie</th>
                    <th>Destinatario</th>
                    <th>Peso</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((ddr) => (
                    <tr key={ddr.id} data-testid={`history-row-${ddr.id}`}>
                      <td>
                        <Badge variant="outline" className="font-mono">
                          {ddr.serie}-{ddr.numero_ddr}
                        </Badge>
                      </td>
                      <td>
                        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
                          <User className="w-3 h-3 mr-1" /> Prod.
                        </Badge>
                      </td>
                      <td>{formatDate(ddr.data_documento)}</td>
                      <td className="max-w-[200px] truncate">{ddr.specie}</td>
                      <td>{ddr.destinazione?.nome || "-"}</td>
                      <td className="font-medium">{ddr.peso_kg} kg</td>
                      <td>
                        <div className="flex gap-1">
                          <Button
                            data-testid={`edit-ddr-${ddr.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/modifica/${ddr.id}`)}
                            className="text-slate-600 hover:text-blue-500"
                            title="Modifica"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                data-testid={`download-history-${ddr.id}`}
                                variant="ghost"
                                size="sm"
                                className="text-slate-600 hover:text-orange-500"
                                title="Scarica PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                data-testid={`download-ddr-${ddr.id}`}
                                onSelect={async () => {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/ddr/${ddr.id}/pdf`;
                                  const response = await fetch(url);
                                  const blob = await response.blob();
                                  const downloadUrl = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = downloadUrl;
                                  a.download = `DDR_${ddr.serie}_${ddr.numero_ddr}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(downloadUrl);
                                }}
                              >
                                <FileText className="w-4 h-4 mr-2 text-orange-500" />
                                Scarica DDR
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                data-testid={`download-ddt-${ddr.id}`}
                                onSelect={async () => {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/ddr/${ddr.id}/ddt`;
                                  const response = await fetch(url);
                                  const blob = await response.blob();
                                  const downloadUrl = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = downloadUrl;
                                  a.download = `DDT_${ddr.serie}_${ddr.numero_ddr}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(downloadUrl);
                                }}
                              >
                                <Truck className="w-4 h-4 mr-2 text-blue-500" />
                                Scarica DDT
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                data-testid={`download-both-${ddr.id}`}
                                onSelect={async () => {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/ddr/${ddr.id}/both`;
                                  const response = await fetch(url);
                                  const blob = await response.blob();
                                  const downloadUrl = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = downloadUrl;
                                  a.download = `DDR_DDT_${ddr.serie}_${ddr.numero_ddr}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(downloadUrl);
                                }}
                              >
                                <Download className="w-4 h-4 mr-2 text-green-500" />
                                Scarica Entrambi
                              </DropdownMenuItem>
                              <div className="my-1 border-t border-slate-200" />
                              <DropdownMenuItem 
                                data-testid={`download-ddr-vuoto-${ddr.id}`}
                                onSelect={async () => {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/ddr/${ddr.id}/pdf?vuoto=true`;
                                  const response = await fetch(url);
                                  const blob = await response.blob();
                                  const downloadUrl = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = downloadUrl;
                                  a.download = `DDR_VUOTO_${ddr.serie}_${ddr.numero_ddr}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(downloadUrl);
                                }}
                              >
                                <FileText className="w-4 h-4 mr-2 text-orange-300" />
                                Scarica DDR Vuoto
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                data-testid={`download-ddt-vuoto-${ddr.id}`}
                                onSelect={async () => {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/ddr/${ddr.id}/ddt?vuoto=true`;
                                  const response = await fetch(url);
                                  const blob = await response.blob();
                                  const downloadUrl = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = downloadUrl;
                                  a.download = `DDT_VUOTO_${ddr.serie}_${ddr.numero_ddr}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(downloadUrl);
                                }}
                              >
                                <Truck className="w-4 h-4 mr-2 text-blue-300" />
                                Scarica DDT Vuoto
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                data-testid={`download-both-vuoto-${ddr.id}`}
                                onSelect={async () => {
                                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/ddr/${ddr.id}/both?vuoto=true`;
                                  const response = await fetch(url);
                                  const blob = await response.blob();
                                  const downloadUrl = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = downloadUrl;
                                  a.download = `DDR_DDT_VUOTO_${ddr.serie}_${ddr.numero_ddr}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(downloadUrl);
                                }}
                              >
                                <Download className="w-4 h-4 mr-2 text-green-300" />
                                Scarica Entrambi Vuoti
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            data-testid={`share-ddr-${ddr.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleShare(ddr)}
                            className="text-slate-600 hover:text-green-500"
                            title="Condividi"
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-btn-${ddr.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(ddr.id)}
                            className="text-slate-600 hover:text-red-500"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo DDR? L'azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default History;
